import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Input from '../../components/input/Input';
import CurrencyInput from '../../components/input/CurrencyInput';
import DatePicker from '../../components/datepicker/DatePicker';
import DropDown from '../../components/dropdown/DropDown';
import MontoTooltipLabel from '../../components/labels/MontoTooltipLabel';
import { MonetizationFormData, FormValues } from '../../pages/MonetizationNew';
import { useFiscalIdConfig } from '../../hooks/useFiscalIdConfig';
import { useState, useRef, useEffect } from 'react';
import { validateDrawerByCuit } from '../../utils/validationUtils';
import TextArea from '../../components/textarea/TextArea';
import AssetSelectionModal from '../../components/modals/AssetSelectionModal';
import OtrasDivisasButton from '../../components/monetization/OtrasDivisasButton';
import AssetLoadedBanner from '../../components/monetization/AssetLoadedBanner';
import { getQuotations } from '../../services/QuotationsService';

const pagareDataGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '16px',
  alignItems: 'end' as const,
};

const clearingOptions = [
  { id: 0, label: 'NO' },
  { id: 1, label: '24 hs' },
  { id: 2, label: '48 hs' },
];

interface PromissoryNoteSectionProps {
  formData: MonetizationFormData;
  formValues: FormValues;
  updateFormValues: (updates: Partial<FormValues>) => void;
  amountInputStatus?: 'normal' | 'error';
  amountInputMessage?: string;
  currencyCode?: string;
  usdQuotation?: number | null;
}

const PromissoryNoteSection = ({ formData, formValues, updateFormValues, amountInputStatus = 'normal', amountInputMessage, currencyCode = 'ARS', usdQuotation }: PromissoryNoteSectionProps) => {
  const fiscalId = useFiscalIdConfig();
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assetName, setAssetName] = useState('');
  const hasAsset = !!formValues.asset_id;
  const showLoadButton = !hasAsset;
  const [drawerError, setDrawerError] = useState<string>('');
  const [lastValidatedCuit, setLastValidatedCuit] = useState<string>('');
  const [drawerManuallyEdited, setDrawerManuallyEdited] = useState(false);
  const [cuitLengthError, setCuitLengthError] = useState<string>('');
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup effect para limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Update due_date and estimated_payment_date when relevant fields change
  useEffect(() => {
    let updatedFields: Partial<FormValues> = {};
    
    // Calculate due_date when issue_date or term_in_days changes
    if (formValues.issue_date && formValues.term_in_days !== undefined && formValues.term_in_days !== null) {
      const issueDate = new Date(formValues.issue_date);
      const dueDate = new Date(issueDate);
      dueDate.setDate(dueDate.getDate() + formValues.term_in_days);
      
      const formattedDueDate = dueDate.toISOString().split('T')[0];
      if (formattedDueDate !== formValues.due_date) {
        updatedFields.due_date = formattedDueDate;
        
        // For promissory notes, estimated_payment_date = due_date (no clearing)
        if (formattedDueDate !== formValues.estimated_payment_date) {
          updatedFields.estimated_payment_date = formattedDueDate;
        }
      }
    }
    
    // Update all changed fields in a single call
    if (Object.keys(updatedFields).length > 0) {
      updateFormValues(updatedFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.issue_date, formValues.term_in_days]);

  const creditorItems = formData.creditors?.map(creditor => ({
    label: creditor.name,
    disabled: false
  })) || [];

  const handleBankingEntityChange = (selectedLabel: string) => {
    const creditor = formData.creditors?.find(c => c.name === selectedLabel);
    if (creditor) {
      updateFormValues({ 
        creditor_id: creditor.id,
        banking_entity_id: creditor.id,
        banking_entity_name: creditor.name
      });
    }
  };

  const handleDrawerCuitChange = async (cuit: string) => {
    // Limpiar timeout previo para la validación de longitud
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Actualizar el valor inmediatamente
    updateFormValues({ 
      drawer: { 
        cuit: cuit,
        company_name: formValues.drawer?.company_name || ''
      } 
    });

    // Limpiar error previo inmediatamente
    setCuitLengthError('');

    // Crear timeout para validación de longitud con delay de 1 segundo
    debounceTimeoutRef.current = setTimeout(() => {
      if (cuit && !fiscalId.isValidDigits(cuit)) {
        setCuitLengthError(fiscalId.invalidLengthMessage);
      } else {
        setCuitLengthError('');
      }
    }, 1000);

    await validateDrawerByCuit({
      cuit,
      formValues,
      updateFormValues,
      setDrawerError,
      setValidatingDrawer: () => {},
      lastValidatedCuit,
      setLastValidatedCuit,
      drawerManuallyEdited,
      setDrawerManuallyEdited,
      debounceTimeoutRef,
      fiscalIdLabel: fiscalId.label,
      isValidDigits: fiscalId.isValidDigits,
    });
  };

  const handleDrawerCompanyNameChange = (companyName: string) => {
    // Marcar que el drawer fue editado manualmente
    setDrawerManuallyEdited(true);
    
    updateFormValues({ 
      drawer: { 
        cuit: formValues.drawer?.cuit || '',
        company_name: companyName 
      } 
    });
    
    // Ejecutar validación para mostrar el mensaje de edición
    if (formValues.drawer?.cuit && fiscalId.isValidDigits(formValues.drawer.cuit)) {
      validateDrawerByCuit({
        cuit: formValues.drawer.cuit,
        formValues: {
          ...formValues,
          drawer: {
            ...formValues.drawer,
            company_name: companyName
          }
        },
        updateFormValues,
        setDrawerError,
        setValidatingDrawer: () => {},
        lastValidatedCuit,
        setLastValidatedCuit,
        drawerManuallyEdited: true,
        setDrawerManuallyEdited,
        debounceTimeoutRef,
        fiscalIdLabel: fiscalId.label,
        isValidDigits: fiscalId.isValidDigits,
      });
    }
  };

  const getSelectedBankingEntity = () => {
    if (formValues.banking_entity_name) {
      return formValues.banking_entity_name;
    }
    const creditor = formData.creditors?.find(c => c.id === formValues.banking_entity_id);
    return creditor?.name || 'Seleccionar entidad bancaria';
  };

  const getSelectedClearing = () => {
    const clearing = clearingOptions.find((option) => option.id === formValues.clearing);
    return clearing?.label || 'Seleccionar';
  };

  const handleClearingChange = (selectedLabel: string) => {
    const selected = clearingOptions.find((option) => option.label === selectedLabel);
    updateFormValues({ clearing: selected?.id });
  };

  const handleObservationChange = (e: { target: { value: string } }) => {
    const value = e.target.value;
    if (value.length <= 2500) {
      updateFormValues({ observation: value });
    }
  };

  // Helper function for endorsed dropdown
  const getSelectedEndorsed = () => {
    if (formValues.endorsed === true) return 'Sí';
    if (formValues.endorsed === false) return 'No';
    return 'Seleccionar';
  };

  const handleEndorsedChange = (selectedLabel: string) => {
    const endorsed = selectedLabel === 'Sí' ? true : false;
    updateFormValues({ endorsed });
  };

  const endorsedItems = [
    { label: 'Sí', disabled: false },
    { label: 'No', disabled: false }
  ];

  useEffect(() => {
    if (!formValues.asset_id || assetName) return;
    getQuotations()
      .then((response) => {
        if (response.success && response.data && Array.isArray(response.data)) {
          const asset = (response.data as Array<{ id: number; name: string }>).find(
            (a) => a.id === formValues.asset_id
          );
          if (asset) setAssetName(asset.name);
        }
      })
      .catch(() => {});
  }, [formValues.asset_id, assetName]);

  const handleAssetAccept = (data: {
    assetId: number;
    assetName: string;
    quantity: number;
    quotation: number;
    calculatedAmount: number;
  }) => {
    setAssetName(data.assetName);
    updateFormValues({
      asset_id: data.assetId,
      asset_quantity: data.quantity,
      asset_quotation: data.quotation,
      monetization_amount: data.calculatedAmount,
    });
    setAssetModalOpen(false);
  };

  const handleAssetClear = () => {
    setAssetName('');
    updateFormValues({
      asset_id: null,
      asset_quantity: null,
      asset_quotation: null,
      monetization_amount: null,
    });
  };

  return (
    <>
      {/* Datos del pagaré */}
      <Card margin={{ bottom: '4' }}>
        <div style={{ backgroundColor: '#E6F5F0', padding: '12px 16px', borderRadius: '8px', margin: '0 0 16px 0' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong' withMargin={false}>
            Datos del pagaré
          </H>
        </div>
        
        {/* Fila 1: Monto bruto | Divisa + Otras divisas | Fecha emisión | Fecha vencimiento */}
        <div
          data-testid="pagare-data-row-1"
          style={{ ...pagareDataGridStyle, marginBottom: hasAsset ? '8px' : '24px' }}
        >
          <Card>
            <MontoTooltipLabel />
            <Card margin={{ top: '1' }}>
              <CurrencyInput
                value={formValues.monetization_amount}
                onValueChange={(val) => updateFormValues({ monetization_amount: val })}
                size="md"
                inputStatus={hasAsset ? 'normal' : amountInputStatus}
                message={hasAsset ? undefined : amountInputMessage}
                readOnly={hasAsset}
              />
            </Card>
          </Card>
          <div style={{ display: 'flex', alignItems: 'end', gap: '8px', minWidth: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input
                label="Divisa"
                value={formData.currency}
                readOnly
                size="md"
                modifier="weak"
              />
            </div>
            {showLoadButton && (
              <OtrasDivisasButton onClick={() => setAssetModalOpen(true)} />
            )}
          </div>
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de emisión *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.issue_date}
                onChange={(date: string) => updateFormValues({ issue_date: date })}
                disabled={false}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de vencimiento *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.due_date}
                onChange={() => {}}
                disabled={true}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
        </div>

        {hasAsset && (
          <div style={{ marginBottom: '24px' }}>
            <AssetLoadedBanner
              assetName={assetName}
              quantity={formValues.asset_quantity ?? 0}
              quotation={formValues.asset_quotation ?? 0}
              calculatedAmount={formValues.monetization_amount ?? 0}
              currencyCode={currencyCode}
              onEdit={() => setAssetModalOpen(true)}
              onClear={handleAssetClear}
            />
          </div>
        )}

        {/* Fila 2: Plazo en días | Entidad bancaria | Endosado | ID E-Pagaré */}
        <div data-testid="pagare-data-row-2" style={{ ...pagareDataGridStyle, marginBottom: '24px' }}>
          <Input
            label="Plazo en días *"
            type="number"
            value={formValues.term_in_days?.toString() || ''}
            onChange={(e) => {
              const value = e.target.value;
              const numValue = value === '' ? 0 : parseInt(value, 10);
              if (!Number.isNaN(numValue)) {
                updateFormValues({ term_in_days: numValue });
              }
            }}
            size="md"
            inputStatus="normal"
          />
          <DropDown
            id="banking_entity"
            label="Entidad bancaria *"
            title={getSelectedBankingEntity()}
            items={creditorItems}
            onChange={handleBankingEntityChange}
            size="md"
            marginTop="4px"
          />
          <DropDown
            id="endorsed"
            label="Endosado *"
            title={getSelectedEndorsed()}
            items={endorsedItems}
            onChange={handleEndorsedChange}
            size="md"
            marginTop="4px"
          />
          <Input
            label="ID E-Pagare *"
            value={formValues.e_promissory_note_id || ''}
            onChange={(e) => updateFormValues({ e_promissory_note_id: e.target.value })}
            size="md"
            maxLength={250}
            inputStatus="normal"
          />
        </div>

        {/* Fila 3: Plazo de clearing | Fecha estimada de pago */}
        <div data-testid="pagare-data-row-3" style={{ ...pagareDataGridStyle }}>
          <DropDown
            id="clearing"
            label="Plazo de clearing *"
            title={getSelectedClearing()}
            items={clearingOptions.map((option) => ({ label: option.label, disabled: false }))}
            onChange={handleClearingChange}
            size="md"
            marginTop="4px"
          />
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha estimada de pago *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.estimated_payment_date || new Date().toISOString().split('T')[0]}
                onChange={() => {}}
                disabled={true}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
        </div>
      </Card>

      {/* Datos de librador */}
      <Card margin={{ bottom: '4' }}>
        <div style={{ backgroundColor: '#E6F5F0', padding: '12px 16px', borderRadius: '8px', margin: '0 0 16px 0' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong' withMargin={false}>
            Datos de librador
          </H>
        </div>
        
        <div data-testid="pagare-drawer-row-1" style={{ ...pagareDataGridStyle, marginBottom: '24px' }}>
          <Input
            label="Librador *"
            value={formValues.drawer?.company_name || ''}
            onChange={(e) => handleDrawerCompanyNameChange(e.target.value)}
            size="md"
            maxLength={250}
            inputStatus="normal"
            placeholder="Librador"
          />
          <Input
            label={`${fiscalId.drawerLabel} *`}
            type="number"
            value={formValues.drawer?.cuit || ''}
            onChange={(e) => handleDrawerCuitChange(e.target.value)}
            size="md"
            maxLength={250}
            onlyNumbers
            inputStatus={cuitLengthError ? 'error' : 'normal'}
          />
        </div>

        {(drawerError || cuitLengthError) && (
          <div style={{ marginTop: '-16px', marginBottom: '24px' }}>
            {cuitLengthError ? (
              <P variant="danger" type="3">
                {cuitLengthError}
              </P>
            ) : null}
            {drawerError ? (
              <P variant="danger" type="3">
                {drawerError}
              </P>
            ) : null}
          </div>
        )}

        <div data-testid="pagare-drawer-row-2">
          <TextArea
            label="Observación"
            value={formValues.observation || ''}
            onChange={handleObservationChange}
            placeHolder="Ingrese observaciones adicionales (opcional)"
            maxLength={2500}
            rows={4}
            maxLengthHelpText={true}
          />
        </div>
      </Card>

      <AssetSelectionModal
        open={assetModalOpen}
        onClose={() => setAssetModalOpen(false)}
        onAccept={handleAssetAccept}
        currencyCode={currencyCode}
        usdQuotation={usdQuotation}
        initialAssetId={formValues.asset_id}
        initialQuantity={formValues.asset_quantity}
        initialQuotation={formValues.asset_quotation}
      />
    </>
  );
};

export default PromissoryNoteSection;